import { useState, useEffect, useCallback, useRef } from 'react';
import type { VisualField, ChatExperienceConfig, PromptSuggestion } from '../../../types/wizardTypes';
import { safeJsonParse } from '../../../utils/security';
import { schemaToFields } from '../converters/fieldSchemaConverter';
import { annotationsToConfig, configToAnnotations, createEmptySuggestion } from '../converters/annotationsConverter';
import JsonEditor from '../../../components/jsonEditor/JsonEditor';
import InfoLabel from '../../../ui/InfoLabel/InfoLabel';
import InputModal from '../../../ui/inputModal/InputModal';
import { Plus, Trash2, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import styles from './ChatExperienceStep.module.css';
import ToggleSwitch from '../../../ui/ToggleSwitch/ToggleSwitch';

interface ChatExperienceStepProps {
  watch: any;
  setValue: any;
  register: any;
  errors: any;
  canEdit: boolean;
  clearErrors: any;
}

export default function ChatExperienceStep({
  watch,
  setValue,
  canEdit,
  clearErrors,
}: ChatExperienceStepProps) {
  const annotationsRaw = watch('annotations') || '{}';
  const [config, setConfig] = useState<ChatExperienceConfig>(() =>
    annotationsToConfig(safeJsonParse(annotationsRaw, {}).data)
  );
  const suppressSync = useRef(false);
  const [showAnnotationsJson, setShowAnnotationsJson] = useState(false);
  const [deleteSuggestionIndex, setDeleteSuggestionIndex] = useState<number | null>(null);

  useEffect(() => {
    const parsed = safeJsonParse(annotationsRaw, {});
    if (parsed.success && parsed.data) {
      const nextConfig = annotationsToConfig(parsed.data);
      suppressSync.current = true;
      setConfig(nextConfig);
    }
  }, [annotationsRaw]);

  useEffect(() => {
    if (suppressSync.current) {
      suppressSync.current = false;
      return;
    }
    const annotationsObj = configToAnnotations(config);
    clearErrors('annotations');
    setValue('annotations', JSON.stringify(annotationsObj, null, 2), { shouldDirty: true });
  }, [config]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateConfig = useCallback((updates: Partial<ChatExperienceConfig>) => {
    setConfig(prev => ({ ...prev, ...updates }));
  }, []);

  const inputSchemaRaw = watch('inputSchema') || '{}';
  const allFields: VisualField[] = (() => {
    const parsed = safeJsonParse(inputSchemaRaw, {});
    if (parsed.success && parsed.data) {
      return schemaToFields(parsed.data);
    }
    return [];
  })();

  const suggestions = config.promptSuggestions.length > 0
    ? config.promptSuggestions
    : [createEmptySuggestion()];

  const addSuggestion = () => {
    updateConfig({ promptSuggestions: [...suggestions, createEmptySuggestion()] });
  };

  const updateSuggestion = (index: number, updates: Partial<PromptSuggestion>) => {
    const updated = suggestions.map((suggestion, suggestionIndex) =>
      suggestionIndex === index ? { ...suggestion, ...updates } : suggestion
    );
    updateConfig({ promptSuggestions: updated });
  };

  const removeSuggestion = (index: number) => {
    const updated = suggestions.filter((_, suggestionIndex) => suggestionIndex !== index);
    updateConfig({ promptSuggestions: updated });
  };

  const confirmRemoveSuggestion = () => {
    if (deleteSuggestionIndex === null) return;
    removeSuggestion(deleteSuggestionIndex);
    setDeleteSuggestionIndex(null);
  };

  const suggestionToDelete =
    deleteSuggestionIndex !== null ? suggestions[deleteSuggestionIndex] : null;
  const deleteMessage = suggestionToDelete?.text?.trim()
    ? `Are you sure you want to delete "${suggestionToDelete.text.trim()}"?\n This will remove it from the quick actions list and update the annotations JSON.`
    : 'Are you sure you want to delete this quick action? This will remove it from the list and update the annotations JSON.';

  const toggleField = (suggestionIndex: number, fieldName: string) => {
    const suggestion = suggestions[suggestionIndex];
    const isSelected = suggestion.requiredFields.includes(fieldName);

    let newRequiredFields: string[];
    let newLabels: Record<string, string>;

    if (isSelected) {
      newRequiredFields = suggestion.requiredFields.filter(field => field !== fieldName);
      newLabels = { ...suggestion.labels };
      delete newLabels[fieldName];
    } else {
      newRequiredFields = [...suggestion.requiredFields, fieldName];
      const schemaField = allFields.find(field => field.name === fieldName);
      newLabels = {
        ...suggestion.labels,
        [fieldName]: schemaField?.label || schemaField?.description || fieldName,
      };
    }

    updateSuggestion(suggestionIndex, {
      requiredFields: newRequiredFields,
      labels: newLabels,
    });
  };

  const updateFieldLabel = (suggestionIndex: number, fieldName: string, label: string) => {
    const suggestion = suggestions[suggestionIndex];
    updateSuggestion(suggestionIndex, {
      labels: { ...suggestion.labels, [fieldName]: label },
    });
  };

  return (
    <div className={styles.step}>
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionHeading}>
            <Sparkles size={16} />
            <h3 className={styles.sectionTitle}>Prompt Suggestions</h3>
            <InfoLabel content="Auto-suggestions shown to users when they start a conversation. Link each suggestion to the fields it requires." />
          </div>
        </div>
        <div className={styles.advancedSection}>
          <div className={styles.sectionHeading}>
            <button
              type="button"
              className={styles.collapseBtn}
              onClick={() => setShowAnnotationsJson(!showAnnotationsJson)}
            >
              {showAnnotationsJson ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              Annotations (JSON)
            </button>
            <InfoLabel content="Raw annotations JSON. Changes here override the visual configuration." />
          </div>
          {showAnnotationsJson && (
            <>

              <JsonEditor
                value={watch('annotations')}
                onChange={(value) => {
                  clearErrors('annotations');
                  setValue('annotations', value, { shouldDirty: true });
                }}
                height="250px"
                title="Annotations"
                readOnly={!canEdit}
              />
            </>
          )}
        </div>
        <div className={styles.suggestionList}>
          {suggestions.map((suggestion, idx) => (
            <div
              key={idx}
              className={`${styles.suggestionCard} ${suggestion.enabled === false ? styles.suggestionCardDisabled : ''}`}
            >
              <div className={styles.suggestionHeader}>
                <span className={styles.promptIcon}>&ldquo;</span>
                <input
                  type="text"
                  className={styles.promptInput}
                  value={suggestion.text}
                  onChange={e => updateSuggestion(idx, { text: e.target.value })}
                  placeholder='e.g. "Analyze inventory risk for supplier"'
                  disabled={!canEdit}
                  autoFocus={canEdit && config.promptSuggestions.length === 0}
                />
                {canEdit && (
                  <>
                    <button
                      type="button"
                      className={styles.removeBtn}
                      onClick={() => setDeleteSuggestionIndex(idx)}
                      aria-label="Delete suggestion"
                      title="Delete quick action"
                    >
                      <Trash2 size={14} />
                    </button>
                    <span title={suggestion.enabled === false ? 'Disabled — hidden from chat' : 'Enabled — shown in chat'}>
                      <ToggleSwitch
                        checked={suggestion.enabled ?? true}
                        onChange={(checked) => updateSuggestion(idx, { enabled: checked })}
                        disabled={!canEdit}
                      />
                    </span>

                  </>
                )}
              </div>

              {allFields.length > 0 && (
                <div className={styles.fieldLinking}>
                  <span className={styles.fieldLinkingLabel}>Linked Fields:</span>
                  <div className={styles.fieldChips}>
                    {allFields.map(field => {
                      const isActive = suggestion.requiredFields.includes(field.name);
                      return (
                        <button
                          key={field.name}
                          type="button"
                          className={`${styles.fieldChip} ${isActive ? styles.fieldChipActive : ''}`}
                          onClick={() => toggleField(idx, field.name)}
                          disabled={!canEdit}
                        >
                          {field.label || field.name}
                        </button>
                      );
                    })}
                  </div>

                  {suggestion.requiredFields.length > 0 && (
                    <div className={styles.labelRows}>
                      {suggestion.requiredFields.map(fieldName => (
                        <div key={fieldName} className={styles.labelRow}>
                          <span className={styles.labelFieldName}>{fieldName}</span>
                          <span className={styles.labelArrow}>-&gt;</span>
                          <input
                            type="text"
                            className={styles.labelInput}
                            value={suggestion.labels[fieldName] || ''}
                            onChange={e => updateFieldLabel(idx, fieldName, e.target.value)}
                            placeholder="Display label"
                            disabled={!canEdit}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {canEdit && (
            <button
              type="button"
              className={styles.addBtn}
              onClick={addSuggestion}
            >
              <Plus size={14} />
              Add Prompt Suggestion
            </button>
          )}
        </div>
      </div>

      <InputModal
        open={deleteSuggestionIndex !== null}
        title="Delete Quick Action"
        confirmText="Delete"
        cancelText="Cancel"
        showInput={false}
        message={deleteMessage}
        onConfirm={confirmRemoveSuggestion}
        onCancel={() => setDeleteSuggestionIndex(null)}
      />

      {/* {requiredFields.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <HelpCircle size={16} />
            <h3 className={styles.sectionTitle}>Missing Field Questions</h3>
          </div>
          <p className={styles.sectionHint}>
            When a required field is missing, what should the assistant ask? Leave blank to use a default question.
          </p>

          <div className={styles.questionList}>
            {requiredFields.map(field => (
              <div key={field.name} className={styles.questionRow}>
                <div className={styles.questionField}>
                  <span className={styles.questionFieldName}>
                    {field.label || field.name}
                  </span>
                  {field.required && <span className={styles.requiredDot} />}
                </div>
                <input
                  type="text"
                  className={styles.questionInput}
                  value={config.missingFieldQuestions[field.name] || ''}
                  onChange={e => updateFieldQuestion(field.name, e.target.value)}
                  placeholder={`e.g. "What ${(field.label || field.name).toLowerCase()} should I use?"`}
                  disabled={!canEdit}
                />
              </div>
            ))}
          </div>
        </div>
      )} */}

      {/* <div className={styles.section}>
        <label className={styles.toggleLabel}>
          <input
            type="checkbox"
            checked={config.confirmBeforeExecute}
            onChange={e => updateConfig({ confirmBeforeExecute: e.target.checked })}
            disabled={!canEdit}
            className={styles.checkbox}
          />
          <div>
            <span className={styles.toggleText}>Confirm before executing</span>
            <span className={styles.toggleHint}>
              Ask the user to confirm before running this tool
            </span>
          </div>
        </label>
      </div> */}


    </div>
  );
}
